import EditNoteIcon from "@mui/icons-material/EditNote";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { Localized } from "@fluent/react";
import { useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import { Alert, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { SaveButton } from "../../../../app/components/SaveButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import { useAppDispatch } from "../../../../app/hooks";
import { createDocumentWorkflowStage, DocumentWorkflowStageTypeEnum, updateDocumentWorkflowStage } from "../documentWorkflowApi";
import { mapStageTypeToApi } from "../documentWorkflowHelpers";
import { UiStageType } from "../documentWorkflowTypes";

type AddEditWorkflowStageProps = {
    open: boolean;
    mode: "add" | "edit";
    documentId?: number | string | null;
    stageType?: UiStageType;
    stageId?: number;
    stageApiType?: DocumentWorkflowStageTypeEnum;
    title: string;
    description: string;
    onTitleChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    onClose: () => void;
    onSuccess: () => void;
};

const stageTypeStyles: Record<UiStageType, { color: string; border: string; background: string }> = {
    EDIT: { color: "#1976D2", border: "#5FA8E8", background: "#EAF4FF" },
    REVIEW: { color: "#EF6C00", border: "#FFB680", background: "#FFF4E8" },
    APPROVAL: { color: "#2E7D32", border: "#74C37A", background: "#EAF7EA" },
};

const stageTypeChipIcons: Record<UiStageType, JSX.Element> = {
    EDIT: <EditNoteIcon />,
    REVIEW: <QuestionAnswerIcon />,
    APPROVAL: <TaskAltOutlinedIcon />,
};

const stageTypeLabels: Record<UiStageType, string> = {
    EDIT: "Opracowanie",
    REVIEW: "Opiniowanie",
    APPROVAL: "Zatwierdzanie",
};

const getStageTypeChipSx = (stageType: UiStageType) => ({
    color: stageTypeStyles[stageType].color,
    borderColor: stageTypeStyles[stageType].border,
    bgcolor: stageTypeStyles[stageType].background,
    borderWidth: 1,
    borderStyle: "solid",
    fontWeight: 600,
    "& .MuiChip-icon": {
        color: "inherit",
    },
});

const createStageDescription = (stageType: UiStageType) => {
    if (stageType === "REVIEW") {
        return "Etap zbierania opinii i uwag do dokumentu.";
    }

    return "Etap finalnej akceptacji i zatwierdzenia dokumentu.";
};

export const AddEditWorkflowStage = ({
    open,
    mode,
    documentId,
    stageType,
    stageId,
    stageApiType,
    title,
    description,
    onTitleChange,
    onDescriptionChange,
    onClose,
    onSuccess,
}: AddEditWorkflowStageProps) => {
    const dispatch = useAppDispatch();
    const [isSaving, setIsSaving] = useState(false);
    const [serverError, setServerError] = useState("");
    const canSave = title.trim() !== "" && description.trim() !== "";

    const getServerErrorMessage = (payload: unknown) => {
        if (payload && typeof payload === "object") {
            const apiPayload = payload as {
                kind?: string;
                problem?: { title?: string };
            };

            if (apiPayload.kind === "http" && apiPayload.problem?.title) {
                return apiPayload.problem.title;
            }
        }

        return "error";
    };

    useEffect(() => {
        if (!open) {
            setIsSaving(false);
            setServerError("");
        }
    }, [open]);

    const handleSaveClick = async () => {
        if (!canSave || isSaving) {
            return;
        }

        const trimmedTitle = title.trim();
        const trimmedDescription = description.trim();

        if (!trimmedTitle || !trimmedDescription) {
            return;
        }

        setIsSaving(true);
        setServerError("");

        try {
            if (mode === "add") {
                if (!documentId || !stageType || stageType === "EDIT") {
                    return;
                }

                const result = await dispatch(
                    createDocumentWorkflowStage({
                        documentId: String(documentId),
                        dto: {
                            name: trimmedTitle,
                            description: trimmedDescription,
                            type: mapStageTypeToApi(stageType),
                        },
                    })
                );

                if (createDocumentWorkflowStage.fulfilled.match(result)) {
                    onSuccess();
                    return;
                }

                setServerError(getServerErrorMessage(result.payload));
                return;
            } else {
                if (!documentId || !stageId || !stageType) {
                    return;
                }

                const resolvedStageType =
                    stageType === "EDIT"
                        ? stageApiType ?? "EDIT"
                        : mapStageTypeToApi(stageType);

                const result = await dispatch(
                    updateDocumentWorkflowStage({
                        documentId: String(documentId),
                        stageId: String(stageId),
                        dto: {
                            name: trimmedTitle,
                            description: trimmedDescription,
                            type: resolvedStageType,
                        },
                    })
                );

                if (updateDocumentWorkflowStage.fulfilled.match(result)) {
                    onSuccess();
                    return;
                }

                setServerError(getServerErrorMessage(result.payload));
            }
        } finally {
            setIsSaving(false);
        }
    };

    return <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle>{mode === "edit" ? "Edycja etapu" : "Nowy etap"}</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 2 }}>
                {mode === "add" && stageType && <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                        p: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: alpha(stageTypeStyles[stageType].border, 0.9),
                        bgcolor: alpha(stageTypeStyles[stageType].background, 0.9),
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                            Dodawany etap:
                        </Typography>
                        <Chip
                            size="small"
                            label={stageTypeLabels[stageType]}
                            icon={stageTypeChipIcons[stageType]}
                            variant="outlined"
                            sx={{ ...getStageTypeChipSx(stageType), borderRadius: 0.75 }}
                        />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {createStageDescription(stageType)}
                    </Typography>
                </Box>}
                <TextField
                    label="Nazwa etapu"
                    fullWidth
                    size="small"
                    value={title}
                    onChange={event => {
                        onTitleChange(event.target.value);
                        if (serverError) {
                            setServerError("");
                        }
                    }}
                />
                <TextField
                    label="Opis etapu"
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    value={description}
                    onChange={event => {
                        onDescriptionChange(event.target.value);
                        if (serverError) {
                            setServerError("");
                        }
                    }}
                />
                {serverError && <Alert sx={{ marginTop: 1 }} severity="error">
                    <Localized id={serverError}>
                        <span>_Server error</span>
                    </Localized>
                </Alert>}
            </Stack>
        </DialogContent>
        <DialogActions>
            <SaveButton loading={isSaving} onClick={handleSaveClick} disabled={!canSave || isSaving} />
            <CancelButton loading={false} onClick={onClose} />
        </DialogActions>
    </Dialog>;
};
